import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, TrendingUp, Edit, Save, X } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingStock, setEditingStock] = useState(null);
  const [newStock, setNewStock] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStock = (productId, variantId, currentStock) => {
    setEditingStock(`${productId}-${variantId}`);
    setNewStock(currentStock.toString());
  };

  const handleSaveStock = async (productId, variantId) => {
    try {
      const stockValue = parseInt(newStock);
      if (isNaN(stockValue) || stockValue < 0) {
        toast.error('Please enter a valid stock quantity');
        return;
      }

      await axios.post(`${API}/products/${productId}/variants/${variantId}/stock?new_stock=${stockValue}`);
      toast.success('Stock updated successfully');
      setEditingStock(null);
      fetchProducts();
    } catch (error) {
      console.error('Error updating stock:', error);
      toast.error('Failed to update stock');
    }
  };

  const handleCancelEdit = () => {
    setEditingStock(null);
    setNewStock('');
  };

  const getStockStatus = (stock, threshold) => {
    if (stock === 0) return { color: 'text-red-600', bg: 'bg-red-100', label: 'Out of Stock' };
    if (stock <= threshold) return { color: 'text-amber-600', bg: 'bg-amber-100', label: 'Low Stock' };
    return { color: 'text-green-600', bg: 'bg-green-100', label: 'In Stock' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="inventory-management">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Inventory Management</h1>
        <p className="text-gray-600 mt-2">Monitor and update stock levels for all product variants</p>
      </div>

      {/* Inventory Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{products.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Low Stock Items</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {products.reduce((count, product) => 
                  count + product.variants.filter(v => v.stock_quantity <= product.low_stock_threshold).length, 0
                )}
              </p>
            </div>
            <div className="bg-amber-100 p-3 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Inventory Value</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                ${products.reduce((sum, product) => {
                  const productValue = product.variants.reduce((variantSum, variant) => 
                    variantSum + (variant.stock_quantity * product.price), 0);
                  return sum + productValue;
                }, 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Product</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Size</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Color</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">SKU</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Stock</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Status</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.flatMap(product => 
                product.variants.map(variant => {
                  const status = getStockStatus(variant.stock_quantity, product.low_stock_threshold);
                  const isEditing = editingStock === `${product.id}-${variant.id}`;
                  
                  return (
                    <tr key={`${product.id}-${variant.id}`} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-4">
                          <img
                            src={product.image_urls[0]}
                            alt={product.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                          <div>
                            <p className="font-medium text-gray-900">{product.name}</p>
                            <p className="text-sm text-gray-500">{product.brand}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900">US {variant.size}</td>
                      <td className="py-4 px-6 text-gray-900">{variant.color}</td>
                      <td className="py-4 px-6 text-gray-500 font-mono text-sm">{variant.sku}</td>
                      <td className="py-4 px-6">
                        {isEditing ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={newStock}
                              onChange={(e) => setNewStock(e.target.value)}
                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                              min="0"
                              data-testid={`stock-input-${variant.id}`}
                            />
                          </div>
                        ) : (
                          <span className="font-semibold text-gray-900">{variant.stock_quantity}</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end space-x-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => handleSaveStock(product.id, variant.id)}
                                className="p-2 text-green-600 hover:text-green-800 transition-colors"
                                data-testid={`save-stock-${variant.id}`}
                              >
                                <Save className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                data-testid={`cancel-edit-${variant.id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleEditStock(product.id, variant.id, variant.stock_quantity)}
                              className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                              data-testid={`edit-stock-${variant.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InventoryManagement;